import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BrandMark, BrandWordmark } from '../../../shared/components/BrandLogo';
import {
  ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, Circle,
  CircleDot, FileText, FolderOpen, Hourglass,
} from 'lucide-react';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/portal`;

const styles = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', system-ui, sans-serif" },
  header: {
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 64,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  brandName: { fontSize: 18, fontWeight: 700, color: '#1e293b' },
  brandSub: { fontSize: 11, color: '#94a3b8' },
  banner: {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    color: '#fff',
    padding: '28px 24px',
    textAlign: 'center',
  },
  bannerLabel: { fontSize: 12, opacity: 0.75, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 },
  bannerTitle: { fontSize: 26, fontWeight: 800, margin: 0 },
  bannerMeta: { marginTop: 8, fontSize: 14, opacity: 0.8 },
  container: { maxWidth: 620, margin: '32px auto 60px', padding: '0 24px' },
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 32,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 16, marginTop: 0 },
  fieldGroup: { marginBottom: 18 },
  verifyBox: { border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb', padding: 14, marginBottom: 18 },
  verifyBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
    border: '1px solid #2563eb', background: '#2563eb', color: '#fff',
  },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
  required: { color: '#ef4444', marginLeft: 2 },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
    background: '#fff',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: 90,
    background: '#fff',
  },
  fileArea: {
    border: '2px dashed #d1d5db',
    borderRadius: 8,
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  },
  fileAreaActive: {
    border: '2px dashed #2563eb',
    background: '#eff6ff',
  },
  fileIcon: { fontSize: 28, marginBottom: 8 },
  fileHint: { fontSize: 12, color: '#94a3b8', marginTop: 6 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  divider: { borderTop: '1px solid #f1f5f9', margin: '24px 0' },
  submitBtn: {
    width: '100%',
    padding: '13px',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '12px 14px',
    color: '#dc2626',
    fontSize: 13,
    marginBottom: 16,
  },
  info: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
    padding: '12px 14px',
    color: '#1d4ed8',
    fontSize: 13,
    marginBottom: 16,
  },
  loadingJob: { textAlign: 'center', padding: '60px 0', color: '#94a3b8' },
};

export default function PortalApply() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loadingJob, setLoadingJob] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    current_role: '',
    experience_years: '0-1',
    location: '',
    work_mode: '',
    cover_note: '',
  });
  const [selectedSlot, setSelectedSlot] = useState('');

  // Email verification. `verifiedFor` records which address the token belongs
  // to, so editing the email after verifying un-verifies it rather than
  // applying under an address nobody proved.
  const [otp, setOtp] = useState({
    sent: false, code: '', token: '', verifiedFor: '', busy: false, note: '', error: '',
  });
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const emailVerified = Boolean(otp.token) && otp.verifiedFor === form.email.trim().toLowerCase();

  const sendCode = async () => {
    if (!form.email.trim()) { setOtp(o => ({ ...o, error: 'Enter your email first.' })); return; }
    setOtp(o => ({ ...o, busy: true, error: '', note: '' }));
    try {
      const res = await fetch(`${API}/apply/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId, email: form.email.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Could not send the code.');
      setOtp(o => ({ ...o, sent: true, busy: false, code: '', note: `Code sent to ${form.email.trim()}.` }));
      setCooldown(60);
    } catch (err) {
      setOtp(o => ({ ...o, busy: false, error: err.message }));
    }
  };

  const verifyCode = async () => {
    setOtp(o => ({ ...o, busy: true, error: '', note: '' }));
    try {
      const res = await fetch(`${API}/apply/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId, email: form.email.trim(), code: otp.code }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Could not verify that code.');
      setOtp(o => ({
        ...o, busy: false, token: d.verification_token, verifiedFor: form.email.trim().toLowerCase(), note: '',
      }));
    } catch (err) {
      setOtp(o => ({ ...o, busy: false, error: err.message }));
    }
  };

  useEffect(() => {
    fetch(`${API}/jobs/${jobId}`)
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(data => { setJob(data); setLoadingJob(false); })
      .catch(() => { setError('Job not found or no longer active.'); setLoadingJob(false); });
  }, [jobId]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleFile = file => {
    if (!file) return;
    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.pdf') && !ext.endsWith('.docx') && !ext.endsWith('.doc')) {
      setError('Please upload a PDF or DOCX file.');
      return;
    }
    setResumeFile(file);
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!resumeFile) { setError('Please upload your resume.'); return; }
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.email.trim()) { setError('Email is required.'); return; }
    if (!form.location.trim()) { setError('Preferred location is required.'); return; }
    if (!form.work_mode) { setError('Please choose a work mode.'); return; }
    if (!emailVerified) { setError('Please verify your email address first.'); return; }

    setSubmitting(true);
    setError('');

    const fd = new FormData();
    fd.append('name', form.name.trim());
    fd.append('email', form.email.trim());
    fd.append('phone', form.phone.trim());
    fd.append('job_id', jobId);
    fd.append('current_role', form.current_role.trim());
    fd.append('experience_years', form.experience_years);
    fd.append('location', form.location.trim());
    fd.append('work_mode', form.work_mode);
    fd.append('cover_note', form.cover_note.trim());
    if (selectedSlot) fd.append('preferred_slot_1', selectedSlot);
    fd.append('resume', resumeFile);
    fd.append('verification_token', otp.token);

    try {
      const res = await fetch(`${API}/apply`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Submission failed. Please try again.');
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  if (loadingJob) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingJob}>Loading job details...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.brand} onClick={() => navigate('/portal')}>
          <BrandMark size={34} />
          <div>
            <BrandWordmark size={18} color="#0f172a" />
            <div style={styles.brandSub}>by Amarta Solutions</div>
          </div>
        </div>
        <button
          onClick={() => navigate('/portal')}
          style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: '#475569' }}
        >
          <ArrowLeft size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Back to Jobs
        </button>
      </header>

      {/* Banner */}
      {job && (
        <div style={styles.banner}>
          <div style={styles.bannerLabel}>Applying for</div>
          <h1 style={styles.bannerTitle}>{job.title}</h1>
          <div style={styles.bannerMeta}>
            {[job.department, job.city && job.country ? `${job.city}, ${job.country}` : (job.city || job.country), job.employment_type]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      )}

      {submitted && (
        <div style={{ ...styles.container, textAlign: 'center' }}>
          <div style={{ ...styles.card, padding: '48px 32px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center', color: '#059669' }}><CheckCircle2 size={56} strokeWidth={1.5} /></div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: '0 0 10px' }}>
              Application Submitted!
            </h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, maxWidth: 420, margin: '0 auto 24px' }}>
              Thank you for applying for <strong>{job?.title}</strong>. Our team will review your resume and
              if you're shortlisted, you'll receive an email with your AI interview link and instructions.
            </p>
            {selectedSlot && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#15803d' }}>
                <CalendarDays size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Your preferred slot has been noted. We'll try to schedule your interview accordingly.
              </div>
            )}
            <button
              onClick={() => navigate('/portal')}
              style={{ ...styles.submitBtn, width: 'auto', padding: '11px 28px', display: 'inline-block' }}
            >
              <ArrowLeft size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Browse More Jobs
            </button>
          </div>
        </div>
      )}

      {!submitted && (
      <div style={styles.container}>
        <div style={styles.card}>
          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <p style={styles.sectionTitle}>Personal Information</p>

            <div className="r-grid-2" style={styles.row}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Full Name <span style={styles.required}>*</span></label>
                <input
                  style={styles.input}
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Email <span style={styles.required}>*</span></label>
                <input
                  style={styles.input}
                  type="email"
                  name="email"
                  value={form.email}
                  readOnly={emailVerified}
                  onChange={e => {
                    handleChange(e);
                    // A token belongs to one address. Changing it starts over.
                    setOtp(o => ({ ...o, sent: false, code: '', token: '', verifiedFor: '', note: '', error: '' }));
                  }}
                  placeholder="john@example.com"
                  required
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            </div>

            {/* We email a code before accepting the application, so the address
                on a candidate record is one somebody actually holds — and so
                "one application per job" means something. */}
            <div style={styles.verifyBox}>
              {emailVerified ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#0e7a52', fontWeight: 600 }}>
                  <CheckCircle2 size={16} /> Email verified
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, color: '#6b7280' }}>
                      {otp.sent ? 'Enter the 6-digit code we emailed you.' : 'Verify your email to continue.'}
                    </span>
                    {!otp.sent && (
                      <button type="button" onClick={sendCode} disabled={otp.busy} style={styles.verifyBtn}>
                        {otp.busy ? 'Sending…' : 'Send code'}
                      </button>
                    )}
                  </div>

                  {otp.sent && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                      <input
                        inputMode="numeric" maxLength={6} placeholder="123456" value={otp.code}
                        onChange={e => setOtp(o => ({ ...o, code: e.target.value.replace(/\D/g, '').slice(0, 6), error: '' }))}
                        style={{ ...styles.input, width: 120, letterSpacing: 3, textAlign: 'center' }}
                      />
                      <button type="button" onClick={verifyCode} disabled={otp.busy || otp.code.length < 6}
                        style={{ ...styles.verifyBtn, opacity: otp.code.length < 6 ? 0.6 : 1 }}>
                        {otp.busy ? 'Checking…' : 'Verify'}
                      </button>
                      <button type="button" onClick={sendCode} disabled={otp.busy || cooldown > 0}
                        style={{ ...styles.verifyBtn, background: '#fff', color: '#374151', border: '1px solid #d1d5db', opacity: cooldown > 0 ? 0.6 : 1 }}>
                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                      </button>
                    </div>
                  )}

                  {otp.note && <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 8 }}>{otp.note}</div>}
                  {otp.error && <div style={{ fontSize: 12.5, color: '#b42318', marginTop: 8 }}>{otp.error}</div>}
                </>
              )}
            </div>

            <div className="r-grid-2" style={styles.row}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  style={styles.input}
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Current Role</label>
                <input
                  style={styles.input}
                  name="current_role"
                  value={form.current_role}
                  onChange={handleChange}
                  placeholder="e.g. Software Engineer"
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Preferred Location *</label>
              <input
                style={styles.input}
                name="location"
                required
                maxLength={200}
                value={form.location}
                onChange={handleChange}
                placeholder="e.g. Bengaluru"
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Work Mode *</label>
              <select style={styles.select} name="work_mode" required value={form.work_mode} onChange={handleChange}>
                <option value="">Select…</option>
                <option value="On-site">On-site</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Years of Experience</label>
              <select style={styles.select} name="experience_years" value={form.experience_years} onChange={handleChange}>
                <option value="0-1">0–1 years (Entry Level)</option>
                <option value="1-3">1–3 years (Junior)</option>
                <option value="3-5">3–5 years (Mid-Level)</option>
                <option value="5-10">5–10 years (Senior)</option>
                <option value="10+">10+ years (Expert)</option>
              </select>
            </div>

            <div style={styles.divider} />
            <p style={styles.sectionTitle}>Application Details</p>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Cover Note</label>
              <textarea
                style={styles.textarea}
                name="cover_note"
                value={form.cover_note}
                onChange={handleChange}
                placeholder="Tell us why you're a great fit for this role..."
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            {job?.available_slots?.length > 0 && (
              <>
                <div style={styles.divider} />
                <p style={styles.sectionTitle}>Preferred Interview Time (optional)</p>
                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: -10, marginBottom: 14 }}>
                  Select a time slot for your AI screening interview. If none suit you, we'll reach out by email to reschedule.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                  {job.available_slots.sort().map(slot => {
                    const dt = new Date(slot);
                    const label = dt.toLocaleString('en-IN', {
                      weekday: 'short', day: 'numeric', month: 'short',
                      year: 'numeric', hour: '2-digit', minute: '2-digit',
                    });
                    const selected = selectedSlot === slot;
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setSelectedSlot(selected ? '' : slot)}
                        style={{
                          textAlign: 'left', padding: '10px 14px', borderRadius: 8,
                          border: selected ? '2px solid #2563eb' : '1px solid #d1d5db',
                          background: selected ? '#eff6ff' : '#fff',
                          color: selected ? '#1d4ed8' : '#374151',
                          fontWeight: selected ? 600 : 400,
                          fontSize: 13, cursor: 'pointer',
                          transition: 'all 0.15s',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {selected ? <CircleDot size={16} strokeWidth={2} /> : <Circle size={16} strokeWidth={2} />}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CalendarDays size={14} strokeWidth={2} /> {label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedSlot && (
                  <p style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>
                    <Check size={12} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px' }} /> You selected this slot. We'll send your interview link to this time.
                  </p>
                )}
              </>
            )}

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Resume <span style={styles.required}>*</span></label>
              <div
                style={{ ...styles.fileArea, ...(dragOver ? styles.fileAreaActive : {}) }}
                onClick={() => document.getElementById('resume-input').click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => {
                  e.preventDefault();
                  setDragOver(false);
                  handleFile(e.dataTransfer.files[0]);
                }}
              >
                <input
                  id="resume-input"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={e => handleFile(e.target.files[0])}
                />
                <div style={{ ...styles.fileIcon, display: 'flex', justifyContent: 'center' }}>
                  {resumeFile ? <FileText size={32} strokeWidth={1.6} /> : <FolderOpen size={32} strokeWidth={1.6} />}
                </div>
                {resumeFile ? (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#059669' }}>{resumeFile.name}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                      {(resumeFile.size / 1024).toFixed(0)} KB — Click to change
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Drop your resume here or click to browse</div>
                    <div style={styles.fileHint}>PDF or DOCX, max 10MB</div>
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              style={{ ...styles.submitBtn, opacity: submitting ? 0.7 : 1, cursor: submitting ? 'wait' : 'pointer' }}
              disabled={submitting}
              onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = '#1d4ed8'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#2563eb'; }}
            >
              {submitting
                ? <><Hourglass size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Submitting your application...</>
                : <>Submit Application <ArrowRight size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /></>}
            </button>

            <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 12 }}>
              Your application will be reviewed. If shortlisted, you'll receive an email with next steps for the interview.
            </p>
          </form>
        </div>
      </div>
      )}
    </div>
  );
}
